import { useCallback, useMemo, useState } from 'react';

/**
 * Walk a cursor-paginated list (`docs/API.md` §1.4).
 *
 * Cursor pagination has no page numbers, so this keeps a stack of the cursors
 * already visited. "Next" pushes; "Previous" pops. That is the only way back
 * without an offset — and offsets are exactly what the API refuses to provide,
 * because rows inserted mid-scan make them skip or repeat records.
 *
 * @param {number} [initialLimit=50]
 */
export function useCursorPages(initialLimit = 50) {
  const [limit, setLimit] = useState(initialLimit);
  /** Cursors for pages already seen. `null` is page one. */
  const [stack, setStack] = useState([null]);

  const cursor = stack[stack.length - 1];
  const pageNumber = stack.length;

  /** @param {string|null} nextCursor from `page.next_cursor` */
  const goNext = useCallback((nextCursor) => {
    if (nextCursor) setStack((s) => [...s, nextCursor]);
  }, []);

  const goPrevious = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  /** Return to page one — call whenever a filter changes, or the cursor is meaningless. */
  const reset = useCallback(() => setStack([null]), []);

  /** Change page size and return to page one — a cursor from a 20-per-page
   * walk doesn't mean anything as a position in a 50-per-page one. */
  const changeLimit = useCallback((nextLimit) => {
    setLimit(nextLimit);
    setStack([null]);
  }, []);

  const params = useMemo(
    () => (cursor ? { limit, cursor } : { limit }),
    [limit, cursor],
  );

  return {
    params, limit, setLimit, changeLimit, cursor, pageNumber,
    canGoPrevious: stack.length > 1,
    goNext, goPrevious, reset,
  };
}
